import { useContext, useEffect, useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, FormControlLabel, Grid, IconButton, Radio, Stack, useTheme } from "@mui/material";
import { EditRounded } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useForm, FormProvider } from "react-hook-form";
import DialogTitle from "@mui/material/DialogTitle";
import { useRecoilState, useRecoilValue } from "recoil";
import DialogContentText from "@mui/material/DialogContentText";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { additionalInfoState, generalInfoState } from "../../../search-dms/state";
import { FormNumberInput, FormRadioGroup, FormTextField } from "../../../common/form-fields-wrappers";
import { updateAdditionalInfo } from "../../../search-dms/dmsSearch.actions";
import { SET_SNACKBAR } from "../../../common/utils";
import { GlobalContext } from "../../../../../util/state/GlobalContextProvider";
import { useHasScope } from "../../../../../config/globalConfig";
import { NetworkScope } from "../../../types";
import { minutesToTime, timeToMinutes } from "./KeylessInfo";
import { yupResolver } from "@hookform/resolvers/yup";
import { ADDITIONAL_INFO_FORM_VALIDATION_SCHEMA } from "../../../search-dms/config";

const AdditionalInfo = () => {
    dayjs.extend(utc);
    dayjs.extend(timezone);
    dayjs.tz.guess();

    const hasScope = useHasScope;
    const theme = useTheme();
    const { t } = useTranslation(['network', 'translation']);
    const { dispatchGlobalData } = useContext(GlobalContext);
    const [additionalInfo, setAdditionalInfo] = useRecoilState(additionalInfoState);
    const generalInfo: any = useRecoilValue(generalInfoState);

    const additionalInfoForm = useForm<any>({
        defaultValues: {
            id: null,
            chargingSite: null,
            lastMinute: null,
            notice: 0,
            keyBox: null,
            deliveryCollection: null,
            afterHours: null,
            //keyLess: null,
            checkInCabin: null,
            note: "",
            howToIta: "",
            howToEng: "",
            shuttleProviderEmail: "",
            rce: null,
            rcetmaxAssignedVehicle: minutesToTime(0),
            rceMaxPreauth: minutesToTime(0)
        },
        resolver: yupResolver(
            ADDITIONAL_INFO_FORM_VALIDATION_SCHEMA
        )
    });

    const isRceTimeLogicWrong = () => {
        return timeToMinutes(additionalInfoForm.watch("rceMaxPreauth")) > timeToMinutes(additionalInfoForm.watch("rcetmaxAssignedVehicle"));
    }

    const onSubmit = (data: any) => {
        const reqParameters = {
            id: additionalInfo.id,
            chargingSite: data.chargingSite === "Yes",
            lastMinute: data.lastMinute === "Yes",
            notice: data.notice,
            keyBox: data.keyBox === "Yes",
            deliveryCollection: data.deliveryCollection === "Yes",
            afterHours: data.afterHours === "Yes",
            checkInCabin: data.checkInCabin === "Yes",
            //keyLess: data.keyLess === "Yes",
            note: data.note,
            howToIta: data.howToIta,
            howToEng: data.howToEng,
            shuttleProviderEmail: data.shuttleProviderEmail,
            rce: data.rce === "Yes",
            rcetmaxAssignedVehicle: timeToMinutes(data.rcetmaxAssignedVehicle),
            rceMaxPreauth: timeToMinutes(data.rceMaxPreauth)
        }
        setOpen(false);
        setIsDisabled(true);
        setAdditionalInfo(reqParameters);
        updateAdditionalInfo(reqParameters)
            .then((res: any) => {
                setAdditionalInfo(res.additionalInfo);
            })
            .catch(() => {
                dispatchGlobalData(SET_SNACKBAR.ERROR);
            });
    }

    const [isDisabled, setIsDisabled] = useState(true);
    const [open, setOpen] = useState(false);
    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    const handleDisableForm = (value: boolean) => {
        setIsDisabled(value);
    }

    const isCategPointRealis: boolean = generalInfo?.dmsCategory?.some((elem: any) => elem.includes("Point Rélais"));

    useEffect(() => {
        additionalInfoForm.setValue("id", additionalInfo?.id);
        additionalInfoForm.setValue("chargingSite", additionalInfo?.chargingSite === true ? "Yes" : "No");
        additionalInfoForm.setValue("lastMinute", additionalInfo?.lastMinute === true ? "Yes" : "No");
        additionalInfoForm.setValue("notice", additionalInfo?.notice);
        additionalInfoForm.setValue("keyBox", additionalInfo?.keyBox === true ? "Yes" : "No");
        additionalInfoForm.setValue("deliveryCollection", additionalInfo?.deliveryCollection === true ? "Yes" : "No");
        additionalInfoForm.setValue("afterHours", additionalInfo?.afterHours === true ? "Yes" : "No");
        //additionalInfoForm.setValue("keyLess",additionalInfo?.keyLess === true ? "Yes" : "No");
        additionalInfoForm.setValue("checkInCabin", additionalInfo?.checkInCabin === true ? "Yes" : "No");
        additionalInfoForm.setValue("note", additionalInfo?.note);
        additionalInfoForm.setValue("howToIta", additionalInfo?.howToIta);
        additionalInfoForm.setValue("howToEng", additionalInfo?.howToEng);
        additionalInfoForm.setValue("shuttleProviderEmail", additionalInfo?.shuttleProviderEmail);
        additionalInfoForm.setValue("rce", additionalInfo?.rce === true ? "Yes" : "No");
        additionalInfoForm.setValue("rcetmaxAssignedVehicle", minutesToTime(additionalInfo?.rcetmaxAssignedVehicle));
        additionalInfoForm.setValue("rceMaxPreauth", minutesToTime(additionalInfo?.rceMaxPreauth));
    }, [additionalInfo])

    return (
        <>
            <FormProvider {...additionalInfoForm}>
                <Grid container direction="row" alignItems="start" columnSpacing={2}>
                    <Grid item xs={12}>
                        <Stack direction="column" spacing={2} justifyContent="flex-end" alignItems="flex-end">
                            {hasScope(NetworkScope.MsMng) && isDisabled &&
                                <IconButton color={"accent"} onClick={() => { handleDisableForm(false) }}>
                                    <EditRounded />
                                </IconButton>
                            }
                        </Stack>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <FormRadioGroup
                            control={additionalInfoForm.control}
                            name="chargingSite"
                            label={t("dmsDetail.additionalInfo.form.label.chargingSite")}
                            disabled={generalInfo?.currentStatus !== "ACTIVATED" || isDisabled}
                        >
                            <FormControlLabel
                                value="Yes"
                                control={<Radio />}
                                label={t("dmsDetail.additionalInfo.form.label.yes")}
                            />
                            <FormControlLabel
                                value="No"
                                control={<Radio />}
                                label={t("dmsDetail.additionalInfo.form.label.no")}
                            />
                        </FormRadioGroup>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <FormRadioGroup
                            control={additionalInfoForm.control}
                            name="lastMinute"
                            label={t("dmsDetail.additionalInfo.form.label.lastMinute")}
                        >
                            <FormControlLabel disabled={isDisabled} value="Yes" control={<Radio />} label={t("dmsDetail.additionalInfo.form.label.yes")} />
                            <FormControlLabel disabled={isDisabled} value="No" control={<Radio />} label={t("dmsDetail.additionalInfo.form.label.no")} />
                        </FormRadioGroup>
                    </Grid>

                    <Grid item xs={6} md={3}>
                        <FormRadioGroup
                            control={additionalInfoForm.control}
                            name="keyBox"
                            label={t("dmsDetail.additionalInfo.form.label.keyBox")}
                        >
                            <FormControlLabel disabled={isDisabled} value="Yes" control={<Radio />} label={t("dmsDetail.additionalInfo.form.label.yes")} />
                            <FormControlLabel disabled={isDisabled} value="No" control={<Radio />} label={t("dmsDetail.additionalInfo.form.label.no")} />
                        </FormRadioGroup>
                    </Grid>

                    <Grid item xs={6} md={3}>
                        <FormNumberInput
                            disabled={false}
                            control={additionalInfoForm.control}
                            name="notice"
                            label={t("dmsDetail.additionalInfo.form.label.notice")}
                            minValue={0}
                        />
                    </Grid>

                    <Grid item xs={6} md={3}>
                        <FormRadioGroup
                            control={additionalInfoForm.control}
                            name="deliveryCollection"
                            label={t("dmsDetail.additionalInfo.form.label.deliveryCollection")}
                        >
                            <FormControlLabel disabled={isDisabled} value="Yes" control={<Radio />} label={t("dmsDetail.additionalInfo.form.label.yes")} />
                            <FormControlLabel disabled={isDisabled} value="No" control={<Radio />} label={t("dmsDetail.additionalInfo.form.label.no")} />
                        </FormRadioGroup>
                    </Grid>

                    <Grid item xs={6} md={3}>
                        <FormRadioGroup
                            control={additionalInfoForm.control}
                            name="afterHours"
                            label={t("dmsDetail.additionalInfo.form.label.afterHours")}
                        >
                            <FormControlLabel disabled={isDisabled} value="Yes" control={<Radio />} label={t("dmsDetail.additionalInfo.form.label.yes")} />
                            <FormControlLabel disabled={isDisabled} value="No" control={<Radio />} label={t("dmsDetail.additionalInfo.form.label.no")} />
                        </FormRadioGroup>
                    </Grid>

                    {/* <Grid item xs={6} md={3}>
                        <FormRadioGroup
                            control={additionalInfoForm.control}
                            name="keyLess"
                            label={t("dmsDetail.additionalInfo.form.label.keyLess")}
                        >
                            <FormControlLabel disabled={isDisabled} value="Yes" control={<Radio />} label={t("dmsDetail.additionalInfo.form.label.yes")} />
                            <FormControlLabel disabled={isDisabled} value="No" control={<Radio />} label={t("dmsDetail.additionalInfo.form.label.no")} />
                        </FormRadioGroup>
                    </Grid> */}

                    <Grid item xs={6} md={3}>
                        <FormRadioGroup
                            control={additionalInfoForm.control}
                            name="checkInCabin"
                            label={t("dmsDetail.additionalInfo.form.label.checkInCabin")}
                        >
                            <FormControlLabel disabled={isDisabled} value="Yes" control={<Radio />} label={t("dmsDetail.additionalInfo.form.label.yes")} />
                            <FormControlLabel disabled={isDisabled} value="No" control={<Radio />} label={t("dmsDetail.additionalInfo.form.label.no")} />
                        </FormRadioGroup>
                    </Grid>

                    <Grid item xs={6} md={3}>
                        <FormTextField
                            disabled={isDisabled}
                            control={additionalInfoForm.control}
                            name="note"
                            label={t("dmsDetail.additionalInfo.form.label.note")}
                        />
                    </Grid>

                    <Grid item xs={6} md={3}>
                        <FormTextField
                            disabled={isDisabled}
                            control={additionalInfoForm.control}
                            name="howToIta"
                            label={t("dmsDetail.additionalInfo.form.label.howToIta")}
                        />
                    </Grid>

                    <Grid item xs={6} md={3}>
                        <FormTextField
                            disabled={isDisabled}
                            control={additionalInfoForm.control}
                            name="howToEng"
                            label={t("dmsDetail.additionalInfo.form.label.howToEng")}
                        />
                    </Grid>

                    {
                        generalInfo?.dmsType?.some((elem: any) => elem.includes("Shuttle")) &&
                        <Grid item xs={6} md={3}>
                            <FormTextField
                                disabled={isDisabled}
                                control={additionalInfoForm.control}
                                name="shuttleProviderEmail"
                                label={t("dmsDetail.additionalInfo.form.label.shuttleProviderEmail")}
                            />
                        </Grid>
                    }


                    {
                        <Grid item xs={6} md={3}>
                            <FormRadioGroup
                                control={additionalInfoForm.control}
                                name="rce"
                                label={t("dmsDetail.additionalInfo.form.label.rce")}
                            >
                                <FormControlLabel disabled={isDisabled || isCategPointRealis} value="Yes" control={<Radio />} label={t("dmsDetail.additionalInfo.form.label.yes")} />
                                <FormControlLabel disabled={isDisabled || isCategPointRealis} value="No" control={<Radio />} label={t("dmsDetail.additionalInfo.form.label.no")} />
                            </FormRadioGroup>
                        </Grid>
                    }

                    {
                        <Grid item xs={6} md={3}>
                            <FormTextField
                                disabled={isDisabled || additionalInfoForm.watch("rce") !== "Yes"}
                                control={additionalInfoForm.control}
                                name="rcetmaxAssignedVehicle"
                                label={t("dmsDetail.additionalInfo.form.label.rcetmaxAssignedVehicle")}
                            />
                        </Grid>
                    }

                    {
                        <Grid item xs={6} md={3}>
                            <FormTextField
                                disabled={isDisabled || additionalInfoForm.watch("rce") !== "Yes"}
                                error={isRceTimeLogicWrong()}
                                control={additionalInfoForm.control}
                                name="rceMaxPreauth"
                                label={t("dmsDetail.additionalInfo.form.label.rceMaxPreauth")}
                            />
                        </Grid>
                    }


                    <Grid item xs={12} sx={{ marginTop: theme.spacing(2) }}>
                        <Stack direction="column" spacing={2} justifyContent="flex-end" alignItems="flex-end">
                            {!isDisabled &&
                                <Button variant="contained" onClick={handleOpen} disabled={isRceTimeLogicWrong()} >
                                    {t("dmsDetail.additionalInfo.button.edit")}
                                </Button>
                            }
                        </Stack>
                    </Grid>
                </Grid>
            </FormProvider>

            <Dialog
                open={open}
                onClose={handleClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    {t("dmsDetail.additionalInfo.dialog.title")}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {t("dmsDetail.additionalInfo.dialog.content")}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} variant={"outlined"} color={"accent"}>
                        {t("dmsDetail.additionalInfo.dialog.action.cancel")}
                    </Button>
                    <Button onClick={additionalInfoForm.handleSubmit(onSubmit)} autoFocus>
                        {t("dmsDetail.additionalInfo.dialog.action.confirm")}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default AdditionalInfo;